import type { TodayVisit } from "./visitsClient";
import { isVisitSettled } from "./visitStatus";

/**
 * The rail's "Recent visits" view, split by where each visit sits in time.
 * "Upcoming" is only what's scheduled ahead; a visit whose start has passed
 * but hasn't settled is underway, not completed — grouping it by schedule
 * alone would file an active 9 AM visit under "Completed" by 2 PM.
 */
export interface VisitHistoryGroups {
  /** Scheduled ahead of `now`, soonest first. */
  upcoming: TodayVisit[];
  /** Underway: not settled, not scheduled ahead (includes unscheduled walk-ins). */
  inProgress: TodayVisit[];
  /** Settled (completed or cancelled), newest first. */
  completed: TodayVisit[];
}

/** Sort instant: when the visit was booked for, else when it was created —
 * an unscheduled walk-in sorts by its real arrival, not as "oldest". Zero
 * (sorts last) only when a pre-BE-2116 backend sends neither. */
const sortMs = (visit: TodayVisit): number => {
  const at = visit.scheduledStart ?? visit.createdAt;
  return at ? new Date(at).getTime() : 0;
};

export const groupVisitHistory = (
  visits: TodayVisit[],
  now: number,
): VisitHistoryGroups => {
  // Only a visit actually booked for the future is "upcoming" — an
  // unscheduled visit's createdAt is always in the past, so it can't be.
  const isUpcoming = (visit: TodayVisit) =>
    !isVisitSettled(visit) &&
    visit.scheduledStart !== null &&
    sortMs(visit) > now;

  return {
    upcoming: visits.filter(isUpcoming).sort((a, b) => sortMs(a) - sortMs(b)),
    inProgress: visits
      .filter((visit) => !isVisitSettled(visit) && !isUpcoming(visit))
      .sort((a, b) => sortMs(b) - sortMs(a)),
    completed: visits
      .filter(isVisitSettled)
      .sort((a, b) => sortMs(b) - sortMs(a)),
  };
};
